import { Badge, Group, Stack, Text, Tooltip } from "@mantine/core";
import { useMemo } from "react";
import {
  dayInTz,
  formatDateTimeInTz,
  formatTimezoneDetail,
  formatTimezoneShort,
  getViewerTimezone,
} from "@mip-tg-bot-turntable/utils";
import { statsRangeHint, trendRangeLabel, type TrendRange } from "./StatsTrendControls.tsx";

type Props = {
  /** 业务时区 = bot.timezone */
  readonly businessTimezone: string;
  /** 业务自然日 YYYY-MM-DD（statsDay） */
  readonly businessDay?: string | undefined;
  readonly range?: TrendRange | undefined;
  /** API 返回的窗口起止 ISO（可选） */
  readonly windowStartAt?: string | null | undefined;
  readonly windowEndAt?: string | null | undefined;
};

/** 窗口起止展示：业务时区可读时间，不用 ISO Z */
function formatWindowRange(
  startIso: string,
  endIso: string,
  businessTz: string,
  bizShort: string,
): string {
  const start = formatDateTimeInTz(startIso, businessTz);
  const end = formatDateTimeInTz(endIso, businessTz);
  return `${start} → ${end} ${bizShort}`;
}

/**
 * 三时区上下文条：
 * - 数据：UTC 存库（不切日）
 * - 业务：bot.timezone 切日 / KPI
 * - 浏览：浏览器本地，仅对照展示
 */
export function StatsTimeContextBar(props: Props) {
  const { businessTimezone, businessDay, range, windowStartAt, windowEndAt } = props;

  const viewerTz = useMemo(() => getViewerTimezone(), []);
  const viewerDay = useMemo(() => dayInTz(viewerTz), [viewerTz]);
  const bizDay = businessDay ?? dayInTz(businessTimezone);
  const dayMismatch = viewerDay !== bizDay;
  const bizShort = formatTimezoneShort(businessTimezone);
  const viewerShort = formatTimezoneShort(viewerTz);

  const windowLabel =
    windowStartAt !== undefined &&
    windowStartAt !== null &&
    windowEndAt !== undefined &&
    windowEndAt !== null
      ? formatWindowRange(windowStartAt, windowEndAt, businessTimezone, bizShort)
      : null;

  const windowTooltip =
    windowStartAt !== undefined &&
    windowStartAt !== null &&
    windowEndAt !== undefined &&
    windowEndAt !== null
      ? `业务: ${formatWindowRange(windowStartAt, windowEndAt, businessTimezone, bizShort)}\n本地: ${formatDateTimeInTz(windowStartAt, viewerTz)} → ${formatDateTimeInTz(windowEndAt, viewerTz)}\nUTC: ${windowStartAt} → ${windowEndAt}`
      : null;

  return (
    <Stack gap={4} mb="sm">
      <Group gap="xs" wrap="wrap" align="center">
        <Badge size="sm" variant="light" color="blue">
          业务 {bizShort}
        </Badge>
        <Text size="xs" c="dimmed">
          {formatTimezoneDetail(businessTimezone)}
          {businessDay !== undefined ? ` · 业务日 ${businessDay}` : null}
          {range !== undefined ? ` · ${trendRangeLabel(range)}` : null}
        </Text>
        {dayMismatch ? (
          <Tooltip label="浏览器本地自然日与业务自然日不同；KPI 仍按业务时区切日">
            <Badge size="sm" variant="filled" color="orange">
              本地日 {viewerDay} ≠ 业务日 {bizDay}
            </Badge>
          </Tooltip>
        ) : (
          <Badge size="sm" variant="outline" color="gray">
            本地 {viewerShort} · 日一致
          </Badge>
        )}
        <Tooltip label="库内时刻均为 timestamptz（UTC）；报表切日与默认列表展示用业务时区">
          <Badge size="sm" variant="outline" color="gray">
            数据 UTC
          </Badge>
        </Tooltip>
      </Group>
      {range !== undefined && (
        <Text size="xs" c="dimmed">
          {statsRangeHint(range)}
          {windowLabel !== null ? (
            <>
              {" · 窗口 "}
              <Tooltip label={windowTooltip} multiline maw={360} withArrow openDelay={200}>
                <Text span size="xs" c="dimmed" style={{ cursor: "help" }}>
                  {windowLabel}
                </Text>
              </Tooltip>
            </>
          ) : null}
        </Text>
      )}
    </Stack>
  );
}
